<template>
    <div class="filter-container">
        <div class="field-column field-column__xlarge">
            <div class="field-container">
                <span class="field-label">Date:</span>
                <date-picker v-model="range" range></date-picker>
            </div>
            <div class="field-container">
                <span class="field-label">Experience: </span>
                <v-select v-model="filterStore.selectedExperience.value" :options="sortedExperiences" :clearable="false"
                    label="name"></v-select>
            </div>
        </div>
        <div class="field-column field-column__xlarge">
            <div class="field-container">
                <span class="field-label">Location: </span>
                <v-select v-model="filterStore.selectedLocation.value" :options="sortedLocations" :clearable="false" label="name">
                </v-select>
            </div>
            <div class="field-container">
                <span class="field-label">X-Cube: </span>
                <v-select v-model="filterStore.selectedXCube.value" :options="sortedXCubes" :clearable="false" label="display">
                </v-select>
            </div>
        </div>
        <div class="field-column">
            <div class="field-container">
                <span class="field-label__large">Total Bookings: </span>
                <div>{{ stats.count }}</div>
            </div>
            <div class="field-container">
                <span class="field-label__large">Total Players: </span>
                <div v-if="stats.total_number_of_players">{{ stats.total_number_of_players }}</div>
                <div v-else>N/A</div>
            </div>

            <div class="field-container">
                <span class="field-label__large">Average Rating: </span>
                <div v-if="stats.average_rating">{{ Number(stats.average_rating).toFixed(1) }}</div>
                <div v-else>N/A</div>
            </div>
            <div v-if="filterStore.selectedLocation.value.id !== 'all'" class="field-container">
                <span class="field-label__large">Average Location Rating: </span>
                <div v-if="stats.average_location_rating">{{ Number(stats.average_location_rating).toFixed(1) }}</div>
                <div v-else>N/A</div>
            </div>
            <div v-if="filterStore.selectedExperience.value.id !== 'all'" class="field-container">
                <span class="field-label__large">Average Playing Time: </span>
                <div v-if="stats.average_duration">{{ Number(stats.average_duration).toFixed(0) }}</div>
                <div v-else>N/A</div>
            </div>
        </div>
        <div class="field-column field-column__large">
            <div class="field-container">
                <span class="field-label">Options:</span>
                <v-select v-model="filterStore.bookingOptions.value" multiple :options="BOOKING_OPTIONS"
                    :selectable="(o) => !o.includes('deleted') || filterStore.bookingOptions.value.every((bo) => !bo.includes('deleted'))">
                </v-select>
            </div>

            <div class="field-container">
                <span class="field-label">Limit: </span>
                <v-select v-model="filterStore.limit.value" :options="[25, 50, 100, 150, 200, 250, 1000, 2500, 10000]"
                    :clearable="false"></v-select>
            </div>
        </div>
    </div>
</template>

<script setup>
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { computed, defineProps } from "vue";
import { useUrlFilters } from "../../composables/useUrlFilters";

dayjs.extend(weekday);

const props = defineProps({
    locations: {
        type: Array,
        default: () => [],
    },
    experiences: {
        type: Array,
        default: () => [],
    },
    stats: {
        type: Object,
        default: () => ({}),
    },
});

const filterStore = useUrlFilters(
    () => props.locations,
    () => props.experiences,
);

const ALL_OPTION = {
    name: "All",
    id: "all",
    display: "All",
};

const BOOKING_OPTIONS = [
    "Only deleted",
    "Include deleted",
    "Voucher",
    "Refund",
    "Demo",
    "Consent To Share",
    "Not Finished"
];

const range = computed({
    get() {
        const [begin, end] = filterStore.range.value;
        return [begin.toDate(), end.toDate()];
    },
    set(newValue) {
        const [begin, end] = newValue;
        filterStore.range.value = [dayjs(begin), dayjs(end)];
    },
});

const sortedLocations = computed(() => {
    return [ALL_OPTION].concat([...props.locations].sort((a, b) => a.name.localeCompare(b.name)));
});

const sortedExperiences = computed(() => {
    return [ALL_OPTION].concat(
        [...props.experiences].sort((a, b) => {
            if (a.id === "all") return -1;
            if (b.id === "all") return 1;
            return a.name.localeCompare(b.name);
        }),
    );
});

const sortedXCubes = computed(() => {
    let xcubes;
    if (filterStore.selectedLocation.value?.id !== ALL_OPTION.id) {
        xcubes = filterStore.selectedLocation.value.xcubes.map((x) => ({
            ...x,
            display: `${x.name} ${x.letter}`,
        }));
    } else {
        xcubes = props.locations.reduce((acc, l) => (
            acc.concat(l.xcubes.map((x) => ({ ...x, location: l.id })))
        ), []);
        xcubes = xcubes.map((x) => {
            const location = props.locations.find((l) => l.id === x.location);
            return {
                ...x,
                display: `${location.name} - ${x.name} ${x.letter}`,
            };
        });
    }
    const result = xcubes.toSorted((a, b) => a.name.localeCompare(b.name));
    return [ALL_OPTION, ...result];
});
</script>

<style lang="scss">
@use "../../colors" as *;

.filter-container {
    display: flex;
    justify-content: flex-start;
    background-color: rgba($color-background, 0.71);
    flex-wrap: wrap;
    border: 1px solid $color-primary;
    border-radius: 0.5rem;
    align-items: center;
    padding: 0.5rem;
}

.vs__search {
    min-width: 100px;
}

.field-column {
    flex-basis: 250px;
    min-width: 200px;
    max-width: 300px;

    &__large {
        flex-basis: 400px;
        min-width: 300px;
        max-width: 500px;

        .vs__dropdown-menu {
            min-width: 300px;
        }
    }

    &__xlarge {
        flex-basis: 500px;
        min-width: 400px;
        max-width: 600px;

        .vs__dropdown-menu {
            min-width: 400px;
        }
    }
}

.field-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    padding: 0.5rem;

    .field-label {
        width: 75px;
        padding-right: 1rem;
        text-align: right;

        &__large {
            width: 175px;
        }
    }
}

</style>